import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { authStore } from "entities/Auth";
import { createParcelSearchQueryParams, parcelSearchCardStore } from "entities/ParcelSearchCard";
import { searchFilterStore } from "entities/SearchFilter";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

import { parcelSearchValueValidator } from "../utils";
import { useParcelSearch } from "./parcelSearch.hook";

type UseParcelRequestStrategyReturnType = {
  handleSearch: (reference: string) => void;
};

type UseParcelRequestStrategyParams = {
  isReferenceEqualToOpenedCard: (reference: string) => boolean;
  searchValue: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  openCard: (card: ShipmentModel) => void;
  showMainView: () => void;
  showIntroView: () => void;
  clearViewHistory: () => void;
};

export const useParcelRequestStrategy = ({
  isReferenceEqualToOpenedCard,
  searchValue,
  setErrorMessage,
  openCard,
  showMainView,
  showIntroView,
  clearViewHistory,
}: UseParcelRequestStrategyParams): UseParcelRequestStrategyReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  // const savedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(parcelSearchCardStore.selectors.searchCards);
  const carrier = useAppSelector(searchFilterStore.selectors.carrier);
  const { parcelSearchRequest, isParcelSearchLoading } = useParcelSearch({ isAuthenticated });

  const validatorMeta = useRef<ReturnType<typeof parcelSearchValueValidator.validate>>(
    parcelSearchValueValidator.validate(searchValue)
  );

  useEffect(() => {
    validatorMeta.current = parcelSearchValueValidator.validate(searchValue);
  }, [searchValue]);

  const getRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    reference: string
  ): (() => void) | null => {
    const carrierScacCode = carrier?.scacCodes[0];
    const reqParams = createParcelSearchQueryParams({
      requestType: "search",
      override: {
        number: reference,
        carrierCode: carrierScacCode,
      },
    });
    if (!reqParams) return null;

    switch (strategy) {
      case "authenticated":
        return () => {
          // roadSearchRequest(
          //   { where: { reference } },
          //   {
          //     onSettled: (response) => {
          //       if (response?.length) {
          //         const data = response[0];
          //         dispatch(roadSavedCardStore.actions.prependSavedCardAction({ data }));
          //         if (data?.reference) {
          //           // scrollToElement(data.reference);
          //           openCard(data);
          //         }
          //       } else {
          //         // const dlm = new DataLayersManager().searchInitiated();
          //         roadSearchRequest(reqParams, {
          //           onSuccess: (response) => {
          //             if (response.requestLimitReached) return;
          //             // dlm.setModel(response).handleSearch();
          //             dispatch(roadSearchCardStore.actions.prependSearchCardAction({ data: response }));
          //             if (response?.reference) {
          //               // scrollToElement(response.reference);
          //               openCard(response);
          //             }
          //           },
          //         });
          //       }
          //     },
          //   }
          // );
          parcelSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              // dlm.setModel(response).handleSearch();
              dispatch(parcelSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
      case "not-authenticated":
      default:
        return () => {
          // const dlm = new DataLayersManager().searchInitiated();
          parcelSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              // dlm.setModel(response).handleSearch();
              dispatch(parcelSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
    }
  };

  /**
   * 1. find existing search card OR
   * 2. if not authenticated - block search OR
   * 3. find existing user card OR
   * 4. search user card in DB OR
   * 5. run search and prepend search card to search cards list
   */
  const performRequest = async (reference: string) => {
    const targetSearchCard = searchCards.find((card) => card.reference === reference);
    if (targetSearchCard) {
      if (targetSearchCard?.reference && targetSearchCard.isSuccess) {
        openCard(targetSearchCard);
      }
      return;
    }

    // const targetSavedCard = savedCards.find((card) => card.reference === reference);
    // if (targetSavedCard) {
    //   if (targetSavedCard?.reference) {
    //     openCard(targetSavedCard);
    //   }
    //   return;
    // }

    if (isParcelSearchLoading) return;

    const requestFunc = getRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", reference);

    requestFunc?.();
  };

  const handleSearch = (reference: string) => {
    const { isValid, errorMessage } = validatorMeta.current;

    if (isValid) {
      if (isReferenceEqualToOpenedCard(reference)) return;
      setErrorMessage("");
      performRequest(reference);
      clearViewHistory();
      return;
    }
    setErrorMessage(errorMessage);
  };

  return {
    handleSearch,
  };
};
